/**
 * OneLattice design tokens — the mission-critical-ui palette.
 *
 * Principles (workflow-designer.md §14, mission-critical-ui):
 *  - Dark mode PRIMARY, light secondary; both WCAG 2.2 AA verified.
 *  - No pure #000 / #fff surfaces in dark — elevated desaturated grays reduce hour-11 fatigue.
 *  - Colour is NEVER the sole signal: every semantic hue below is paired with an icon/shape/text
 *    at the point of use. The hues here are chosen to remain distinguishable under deuteranopia.
 *  - Reserve saturation for meaning: chrome is calm/desaturated so status signals pop.
 *
 * This module is the single source of truth. antdTheme.ts feeds these into AntD's ConfigProvider;
 * the canvas (lanes, nodes, edges) reads them directly.
 */

export type ThemeMode = 'dark' | 'light';

/** Semantic role of a lifecycle status, used to colour status-edges and pills. */
export type StatusIntent = 'positive' | 'attention' | 'negative' | 'neutral' | 'info';

export interface Palette {
  /** App canvas background (lowest elevation). */
  bgBase: string;
  /** Card / container surface (one step up). */
  bgContainer: string;
  /** Panels, popovers, inspector (two steps up). */
  bgElevated: string;
  /** Subtle fill (hover rows, chips). */
  bgSubtle: string;
  borderStrong: string;
  border: string;
  borderSubtle: string;
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textInverse: string;
  primary: string;
  primaryHover: string;
  primaryActive: string;
  focusRing: string;
  /** Semantic intents (paired with icons everywhere). */
  positive: string;
  attention: string;
  negative: string;
  info: string;
  processing: string;
}

/** Dark palette — primary theme. Contrast ratios validated against bgBase / bgContainer. */
export const darkPalette: Palette = {
  bgBase: '#0f1418', // deep desaturated blue-gray (never pure black)
  bgContainer: '#171e24',
  bgElevated: '#1e272e',
  bgSubtle: '#212c34',
  borderStrong: '#39454e',
  border: '#2a343c',
  borderSubtle: '#222c33',
  textPrimary: '#e6edf3', // ~13:1 on bgBase
  textSecondary: '#a3b3c0', // ~7:1
  textTertiary: '#71818e', // ~4.6:1 — meets AA for normal text
  textInverse: '#0f1418',
  primary: '#4d9fd6',
  primaryHover: '#63b0e2',
  primaryActive: '#3d8cc2',
  focusRing: '#7cc0ff',
  positive: '#4aa564', // COMPLETED / PASS
  attention: '#d1a23c', // warning / MATERIAL
  negative: '#d46a6a', // FAILED / REFUSED (calm, not alarm-red)
  info: '#5aa0dd',
  processing: '#8a7cd6',
};

/** Light palette — secondary theme. */
export const lightPalette: Palette = {
  bgBase: '#f4f6f8',
  bgContainer: '#ffffff',
  bgElevated: '#ffffff',
  bgSubtle: '#eef1f4',
  borderStrong: '#c2ccd4',
  border: '#d8e0e6',
  borderSubtle: '#e6ecf0',
  textPrimary: '#1a2228', // ~14:1 on white
  textSecondary: '#495663', // ~7:1
  textTertiary: '#6b7885', // ~4.7:1
  textInverse: '#ffffff',
  primary: '#1f6fb2',
  primaryHover: '#2a80c8',
  primaryActive: '#175c95',
  focusRing: '#1f6fb2',
  positive: '#2f8a4c',
  attention: '#9a6f16',
  negative: '#b23b3b',
  info: '#1f6fb2',
  processing: '#6a5bc0',
};

export const palettes: Record<ThemeMode, Palette> = {
  dark: darkPalette,
  light: lightPalette,
};

/** Typography scale (px). Compact, dense-but-legible for long-shift reading. */
export const typography = {
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  fontFamilyMono:
    "'JetBrains Mono', ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
  sizeXs: 11,
  sizeSm: 12,
  size: 13, // base — dense enterprise default
  sizeLg: 15,
  sizeH5: 16,
  sizeH4: 19,
  sizeH3: 23,
  sizeH2: 28,
  lineHeight: 1.5,
} as const;

/** 4px spacing grid. */
export const spacing = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const;

export const radius = {
  sm: 4,
  md: 6,
  lg: 8,
  pill: 999,
} as const;

/**
 * The eight lifecycle stages (flow-definition.v1 §lifecycleStage). Ordered left→right as the
 * canvas swimlanes — time-reads-right (workflow-designer.md §4.2). ACTIVATION is retained per
 * RT-19 (regulatorily load-bearing) but rendered as a slim GATE STRIP on the
 * Decisioning→Monitoring boundary, not a full lane.
 */
export type LifecycleStage =
  | 'INTAKE'
  | 'ONBOARDING'
  | 'DECISIONING'
  | 'ACTIVATION'
  | 'MONITORING'
  | 'REMEDIATION'
  | 'REVIEW'
  | 'EXIT';

export interface StageMeta {
  key: LifecycleStage;
  label: string;
  short: string;
  /** Lane accent hue (header + node-less empty-state). */
  accent: string;
  /** One-sentence purpose shown in the designed empty state (spec §4.2). */
  emptyPrompt: string;
  /** ACTIVATION renders as a narrow gate strip, not a full lane. */
  isGate?: boolean;
}

export const LIFECYCLE_STAGES: StageMeta[] = [
  {
    key: 'INTAKE',
    label: 'Intake',
    short: 'INT',
    accent: '#5b7a9d',
    emptyPrompt: 'Capture the request. Add a form or document-collection to open the case.',
  },
  {
    key: 'ONBOARDING',
    label: 'Onboarding',
    short: 'ONB',
    accent: '#3f9d94',
    emptyPrompt: 'Verify identity and gather evidence — the corpus modal stage.',
  },
  {
    key: 'DECISIONING',
    label: 'Decisioning',
    short: 'DEC',
    accent: '#6d6cc4',
    emptyPrompt: 'Score, gate, and route. Add a risk-rating or decision node.',
  },
  {
    key: 'ACTIVATION',
    label: 'Activation',
    short: 'ACT',
    accent: '#c8a13c',
    emptyPrompt: 'Stage gate — activation controls (e-sign, penny-drop, concurrent-audit) attach here.',
    isGate: true,
  },
  {
    key: 'MONITORING',
    label: 'Monitoring',
    short: 'MON',
    accent: '#3f8fb0',
    emptyPrompt: 'Add perpetual monitoring — enrol the subject in screening re-checks. 59 legacy flows paid for this as a flag.',
  },
  {
    key: 'REMEDIATION',
    label: 'Remediation',
    short: 'REM',
    accent: '#c96b7a',
    emptyPrompt: 'Route failed paths here — RFI loops, outreach, manual fixes.',
  },
  {
    key: 'REVIEW',
    label: 'Review',
    short: 'REV',
    accent: '#8a6cc4',
    emptyPrompt: 'Periodic & event-driven review. The pKYC materiality loop lives here.',
  },
  {
    key: 'EXIT',
    label: 'Exit',
    short: 'EXT',
    accent: '#6f8a7a',
    emptyPrompt: 'Offboarding, erasure, and account closure paths.',
  },
];

export const stageMeta = (stage: string): StageMeta =>
  LIFECYCLE_STAGES.find((s) => s.key === stage) ??
  ({
    key: 'ONBOARDING',
    label: stage,
    short: stage.slice(0, 3),
    accent: '#5b7a9d',
    emptyPrompt: '',
  } as StageMeta);

/**
 * Node-kind visual language (flow-definition.v1 §nodeKind). Each kind gets an accent hue, a glyph
 * (colour is never the sole differentiator), and a shape family. AI primitives share the "spark"
 * hue and glyph. Kinds after `terminal` are RESERVED (compiler-rejected OL-IR-002) except llmTask
 * and confidenceGate which v1.1 IMPLEMENTS.
 */
export type NodeKind =
  | 'capability'
  | 'decision'
  | 'parallel'
  | 'join'
  | 'humanTask'
  | 'subFlow'
  | 'multiSourceScoring'
  | 'terminal'
  | 'llmTask'
  | 'confidenceGate'
  | 'agentLoop'
  | 'humanReviewTask'
  | 'shadowEval'
  | 'retrievalTask'
  | 'memoryTask'
  | 'policyGuard'
  | 'dynamicBranch'
  | 'agentNegotiation';

export interface NodeKindMeta {
  kind: NodeKind;
  label: string;
  accent: string;
  /** Lucide-ish / emoji-free glyph token resolved to an AntD icon at render. */
  glyph: string;
  /** True for the AI-primitive family (spark treatment). */
  ai?: boolean;
  /** RESERVED in the Wave-3 compiler (rendered muted + dashed). */
  reserved?: boolean;
}

const AI_HUE = '#b56db0';

export const NODE_KIND_META: Record<NodeKind, NodeKindMeta> = {
  capability: { kind: 'capability', label: 'Capability', accent: '#3f8fb0', glyph: 'api' },
  decision: { kind: 'decision', label: 'Decision', accent: '#d1a23c', glyph: 'branch' },
  parallel: { kind: 'parallel', label: 'Parallel split', accent: '#6d6cc4', glyph: 'split' },
  join: { kind: 'join', label: 'Join', accent: '#6d6cc4', glyph: 'merge' },
  humanTask: { kind: 'humanTask', label: 'Human task', accent: '#8a6cc4', glyph: 'user' },
  subFlow: { kind: 'subFlow', label: 'Sub-flow', accent: '#3f9d94', glyph: 'partition' },
  multiSourceScoring: {
    kind: 'multiSourceScoring',
    label: 'Multi-source scoring',
    accent: '#5b7a9d',
    glyph: 'scoring',
  },
  terminal: { kind: 'terminal', label: 'Terminal', accent: '#7c8b96', glyph: 'flag' },
  llmTask: { kind: 'llmTask', label: 'LLM task', accent: AI_HUE, glyph: 'spark', ai: true },
  confidenceGate: {
    kind: 'confidenceGate',
    label: 'Confidence gate',
    accent: AI_HUE,
    glyph: 'gate',
    ai: true,
  },
  agentLoop: { kind: 'agentLoop', label: 'Agent loop', accent: AI_HUE, glyph: 'spark', ai: true, reserved: true },
  humanReviewTask: { kind: 'humanReviewTask', label: 'Human review of AI', accent: AI_HUE, glyph: 'user', ai: true, reserved: true },
  shadowEval: { kind: 'shadowEval', label: 'Shadow eval', accent: AI_HUE, glyph: 'spark', ai: true, reserved: true },
  retrievalTask: { kind: 'retrievalTask', label: 'Retrieval', accent: AI_HUE, glyph: 'spark', ai: true, reserved: true },
  memoryTask: { kind: 'memoryTask', label: 'Memory', accent: AI_HUE, glyph: 'spark', ai: true, reserved: true },
  policyGuard: { kind: 'policyGuard', label: 'Policy guard', accent: AI_HUE, glyph: 'gate', ai: true, reserved: true },
  dynamicBranch: { kind: 'dynamicBranch', label: 'Fan-out', accent: '#3f9d94', glyph: 'partition' },
  agentNegotiation: { kind: 'agentNegotiation', label: 'Agent negotiation', accent: AI_HUE, glyph: 'spark', ai: true, reserved: true },
};

export const nodeKindMeta = (kind: string): NodeKindMeta =>
  NODE_KIND_META[kind as NodeKind] ?? NODE_KIND_META.capability;

/**
 * Status-intent mapping for status-edge pills. The status vocabulary is a closed, tenant-defined
 * enum (CORPUS-07 — no synonym invention); we map common tokens to an intent for colour, and fall
 * back to NEUTRAL for anything unknown (the token still renders verbatim, canonical-on-hover).
 */
const STATUS_INTENT: Record<string, StatusIntent> = {
  COMPLETED: 'positive',
  PASS: 'positive',
  PROVIDED: 'positive',
  CONFIRM_MATERIAL: 'attention',
  APPROVED: 'positive',
  BAND_UNCHANGED: 'neutral',
  IMMATERIAL: 'neutral',
  MATERIAL: 'attention',
  BAND_CHANGED: 'attention',
  ESCALATE: 'negative',
  FAILED: 'negative',
  REFUSED: 'negative',
  DISMISS: 'negative',
  NO_RESPONSE: 'negative',
  REJECTED: 'negative',
};

export const statusIntent = (status: string): StatusIntent =>
  STATUS_INTENT[status?.toUpperCase?.()] ?? 'info';

export const intentColor = (intent: StatusIntent, p: Palette): string => {
  switch (intent) {
    case 'positive':
      return p.positive;
    case 'attention':
      return p.attention;
    case 'negative':
      return p.negative;
    case 'info':
      return p.info;
    case 'neutral':
    default:
      return p.textTertiary;
  }
};

/** Edge-kind visual constants (workflow-designer.md §4.5 — three kinds, no fourth). */
export const EDGE = {
  sequence: { width: 1.75, dash: undefined as string | undefined },
  status: { width: 2, dash: undefined as string | undefined },
  /** Data layer: dashed, muted, NEVER implies gating; toggleable via `D`. */
  data: { width: 1.5, dash: '5 4', opacity: 0.55 },
} as const;
